import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import axios from "axios";
import {
  ArrowLeft,
  ShoppingBag,
  Loader2,
  Package,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = filter === "all" 
    ? products 
    : products.filter(p => p.category === filter);

  const featuredProducts = products.filter(p => p.featured);

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Navigation */}
      <nav className="nav-wrapper" data-testid="shop-nav">
        <div className="container-padding py-4 flex items-center justify-between max-w-7xl mx-auto">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#0F3D2E] flex items-center justify-center">
              <span className="text-white font-semibold text-lg">S</span>
            </div>
            <span className="font-semibold text-lg tracking-tight">
              Stone Sills Direct
            </span>
          </Link>

          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-[#57534E] hover:text-[#0F3D2E]"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-[#0F3D2E] text-white py-16">
        <div className="container-padding max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <ShoppingBag className="w-10 h-10" />
            <h1 className="text-4xl font-medium">Shop</h1>
          </div>
          <p className="text-white/80 max-w-2xl">
            Premium quartz and granite shelving kits, chopping boards, hot pan stands and accessories. 
            Ready to order, delivered to your door.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-padding max-w-7xl mx-auto py-12" data-testid="shop-page">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[#0F3D2E]" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 text-[#57534E] mx-auto mb-4" />
            <h2 className="text-2xl font-medium mb-2">Coming Soon</h2>
            <p className="text-[#57534E] mb-6">
              Our shelving kit range is being prepared. Check back soon!
            </p>
            <Link to="/calculator/shelving">
              <Button className="btn-primary">
                Get a Custom Quote
              </Button>
            </Link>
          </div>
        ) : (
          <>
            {/* Featured Products */}
            {featuredProducts.length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-medium mb-6">Featured Kits</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {featuredProducts.map((product, index) => (
                    <ProductCard key={product.id} product={product} index={index} featured />
                  ))}
                </div>
              </div>
            )}

            {/* All Products */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-medium">All Products</h2>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { key: "all", label: "All" },
                    { key: "shelving_kit", label: "Shelving Kits" },
                    { key: "chopping_board", label: "Chopping Boards" },
                    { key: "hot_pan_stand", label: "Hot Pan Stands" },
                    { key: "accessory", label: "Accessories" },
                  ].map((cat) => (
                    <Button
                      key={cat.key}
                      variant={filter === cat.key ? "default" : "outline"}
                      size="sm"
                      onClick={() => setFilter(cat.key)}
                      className={filter === cat.key ? "bg-[#0F3D2E]" : ""}
                      data-testid={`filter-${cat.key}`}
                    >
                      {cat.label}
                    </Button>
                  ))}
                </div>
              </div>

              {filteredProducts.length === 0 ? (
                <p className="text-[#57534E] text-center py-8">No products in this category yet.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredProducts.map((product, index) => (
                    <ProductCard key={product.id} product={product} index={index} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Custom Quote CTA */}
        <div className="mt-16 p-8 bg-[#F5F5F4] text-center">
          <h3 className="text-xl font-medium mb-2">Need a Custom Size?</h3>
          <p className="text-[#57534E] mb-4">
            Use our calculator to get an instant quote for bespoke shelving dimensions.
          </p>
          <Link to="/calculator/shelving">
            <Button className="btn-primary">
              Custom Shelving Calculator
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

const ProductCard = ({ product, index, featured = false }) => {
  const hasDiscount = product.sale_price && product.sale_price < product.price;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
    >
      <Link to={`/shop/${product.slug || product.id}`} data-testid={`product-${product.id}`}>
        <div className={`stone-card p-0 overflow-hidden group cursor-pointer ${featured ? 'border-[#C27C2E]' : ''}`}>
          {/* Image */}
          <div className="aspect-square bg-[#F5F5F4] relative overflow-hidden">
            {product.images && product.images.length > 0 ? (
              <img
                src={product.images[0].url}
                alt={product.images[0].alt || product.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package className="w-16 h-16 text-[#D6D3D1]" />
              </div>
            )}
            
            {/* Badges */}
            <div className="absolute top-3 left-3 flex flex-col gap-2">
              {featured && (
                <span className="px-2 py-1 bg-[#C27C2E] text-white text-xs uppercase">
                  Featured
                </span>
              )}
              {hasDiscount && (
                <span className="px-2 py-1 bg-red-600 text-white text-xs uppercase">
                  Sale
                </span>
              )}
              {product.stock_status === 'low_stock' && (
                <span className="px-2 py-1 bg-amber-500 text-white text-xs uppercase">
                  Low Stock
                </span>
              )}
              {product.stock_status === 'out_of_stock' && (
                <span className="px-2 py-1 bg-gray-600 text-white text-xs uppercase">
                  Out of Stock
                </span>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            <h3 className="font-medium text-lg mb-1 group-hover:text-[#0F3D2E] transition-colors">
              {product.name}
            </h3>
            {product.short_description && (
              <p className="text-sm text-[#57534E] mb-3 line-clamp-2">
                {product.short_description}
              </p>
            )}
            {product.shelf_dimensions && (
              <p className="text-xs text-[#57534E] mb-2">
                {product.shelf_dimensions}
              </p>
            )}
            <div className="flex items-baseline gap-2">
              {hasDiscount ? (
                <>
                  <span className="text-xl font-medium text-[#0F3D2E]">
                    £{product.sale_price.toFixed(2)}
                  </span>
                  <span className="text-sm text-[#57534E] line-through">
                    £{product.price.toFixed(2)}
                  </span>
                </>
              ) : (
                <span className="text-xl font-medium text-[#0F3D2E]">
                  £{product.price.toFixed(2)}
                </span>
              )}
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default Shop;
